const OCR_NOISE_TOKEN = /^[/\\_+=|.,:;'"`~!@#$%^&*()[\]{}<>?-]+$/;
const ALPHANUMERIC = /[A-Za-z0-9]/;

function isOcrNoiseToken(token: string) {
	const trimmed = token.trim();
	if (!trimmed) return true;
	if (ALPHANUMERIC.test(trimmed)) return false;
	return OCR_NOISE_TOKEN.test(trimmed);
}

function cleanText(value: any) {
	return (
		String(value || '')
			// eslint-disable-next-line no-control-regex
			.replace(/[\u0000-\u001F\u007F]+/g, ' ')
			.split(/\s+/)
			.filter((token) => !isOcrNoiseToken(token))
			.join(' ')
			.replace(/\s+/g, ' ')
			.trim()
	);
}

function normalizeCoordinateValue(value: any): any {
	if (Array.isArray(value)) {
		if (!value.length) return null;
		const normalized = value.map((item) => normalizeCoordinateValue(item));
		return normalized.some((item) => item == null) ? null : normalized;
	}

	if (value && typeof value === 'object') {
		const keys = Object.keys(value).sort();
		if (!keys.length) return null;
		const normalized: any = {};
		for (const key of keys) {
			const coordinate = normalizeCoordinateValue(value[key]);
			if (coordinate == null) return null;
			normalized[key] = coordinate;
		}
		return normalized;
	}

	const numeric = Number(value);
	return Number.isFinite(numeric) ? String(numeric) : null;
}

function coordinateKey(value: any) {
	const normalized = normalizeCoordinateValue(value);
	if (normalized == null) return null;
	return typeof normalized === 'string' ? normalized : JSON.stringify(normalized);
}

function bestCoordinateKey(file: any, parentFile?: any) {
	return (
		coordinateKey(file?.keywordCoordinates) ||
		coordinateKey(parentFile?.keywordCoordinates) ||
		coordinateKey(file?.wordCoordinates) ||
		coordinateKey(parentFile?.wordCoordinates) ||
		coordinateKey(file?.coordinates) ||
		coordinateKey(parentFile?.coordinates)
	);
}

function normalizeVersion(value: any) {
	const numeric = Number(value);
	return Number.isFinite(numeric) ? String(numeric) : String(value || '');
}

function dedupeKey(keyword: any, file: any, parentFile?: any) {
	const coordinates = bestCoordinateKey(file, parentFile);
	if (!coordinates) return null;
	const pageNumber = file?.pageNumber != null ? file.pageNumber : parentFile?.pageNumber;
	const versionNumber =
		file?.versionNumber != null ? file.versionNumber : parentFile?.versionNumber;
	const projectArea = String(
		file?.projectArea != null ? file.projectArea : parentFile?.projectArea ?? '',
	)
		.replace(/\s+/g, ' ')
		.trim()
		.toLocaleLowerCase();
	return [
		String(keyword || ''),
		projectArea,
		String(pageNumber || ''),
		normalizeVersion(versionNumber),
		coordinates,
	].join('|');
}

function hasWorkflowMetadata(file: any) {
	return Boolean(
		file?.comment ||
			file?.adjustedCoordinates ||
			file?.toMove ||
			file?.toDelete ||
			file?.isMoved ||
			file?.isDeleted ||
			file?.movedFrom,
	);
}

function priority(file: any, originalText: string) {
	let score = 0;
	if (hasWorkflowMetadata(file)) score += 4;
	if (file?.text === originalText) score += 2;
	return score;
}

function dedupeRows(keyword: any, rows: any[], parentFile?: any) {
	const deduped: any[] = [];
	const indexByKey = new Map<string, number>();

	rows.forEach((row) => {
		if (!row || typeof row !== 'object') {
			deduped.push(row);
			return;
		}

		const originalText = String(row.text || '');
		row.text = cleanText(row.text);
		const key = dedupeKey(keyword, row, parentFile);
		if (!key) {
			deduped.push(row);
			return;
		}

		const existingIndex = indexByKey.get(key);
		if (existingIndex == null) {
			indexByKey.set(key, deduped.length);
			deduped.push(row);
			return;
		}

		const existing = deduped[existingIndex];
		if (priority(row, originalText) > priority(existing, String(existing?.text || ''))) {
			deduped[existingIndex] = row;
		}
	});

	return deduped;
}

function normalizeKeywordResult(result: any) {
	if (!result || !Array.isArray(result.files)) return;

	const detailGroups: any[] = [];
	const detailGroupIndex = new Map<string, number>();
	const files: any[] = [];

	dedupeRows(result.keyword, result.files).forEach((file) => {
		if (!file || typeof file !== 'object') {
			files.push(file);
			return;
		}

		if (Array.isArray(file.details) && file.details.length === 0) {
			delete file.details;
		}

		if (!Array.isArray(file.details) || file.details.length === 0) {
			files.push(file);
			return;
		}

		file.text = cleanText(file.text);
		const firstDetail = file.details[0] || {};
		const firstDetailText = file.details.length ? cleanText(firstDetail?.text) : '';
		const detailArea = String(firstDetail.projectArea ?? file.projectArea ?? '')
			.replace(/\s+/g, ' ')
			.trim()
			.toLocaleLowerCase();
		const textKey = [
			file.text || firstDetailText,
			detailArea,
			firstDetail.pageNumber ?? file.pageNumber ?? '',
			firstDetail.versionNumber ?? file.versionNumber ?? '',
		].join('\u001f');
		const existingIndex = detailGroupIndex.get(textKey);
		if (existingIndex == null) {
			detailGroupIndex.set(textKey, files.length);
			detailGroups.push(file);
			files.push(file);
			return;
		}

		const existing = files[existingIndex];
		existing.details = [
			...(Array.isArray(existing.details) ? existing.details : []),
			...file.details,
		];
	});

	detailGroups.forEach((file) => {
		(file.details || []).forEach((detail: any) => {
			if (detail.projectArea == null) detail.projectArea = file.projectArea ?? null;
		});
		file.details = dedupeRows(result.keyword, file.details || [], file);
	});

	result.files = files.filter(
		(file) => !file || !Array.isArray(file.details) || file.details.length > 0,
	);
}

export function normalizeKeywordResultsPayload<T>(payload: T): T {
	const seen = new WeakSet<Record<string, unknown>>();

	function visit(value: any) {
		if (!value || typeof value !== 'object' || seen.has(value)) return;
		seen.add(value);

		if (Array.isArray(value.files)) {
			normalizeKeywordResult(value);
		}

		Object.keys(value).forEach((key) => visit(value[key]));
	}

	visit(payload);
	return payload;
}
